import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, X, Printer, Truck, MessageSquare, MapPin, Ban, TrendingDown, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { isUS, countryName } from "@/lib/countries";

const CANCEL_REASONS = [
  "Item is out of stock",
  "Item is damaged or defective",
  "Pricing or listing error",
  "Unable to ship to the buyer's address",
  "Buyer requested cancellation",
  "Other",
];

const CARRIERS = [
  "USPS", "UPS", "FedEx", "DHL Express", "Royal Mail", "Canada Post",
  "Australia Post", "Aramex", "PostNL", "India Post", "China Post", "EMS", "SF Express", "Other",
];

function openLabelWindow(label) {
  const w = window.open("", "_blank", "width=460,height=680");
  if (!w) { toast.error("Allow pop-ups to print the label"); return; }
  const fmt = (a, fallbackName) => {
    if (!a) return `<b>${fallbackName || ""}</b>`;
    const l2 = a.street2 ? ` ${a.street2}` : "";
    return `<b>${a.name || fallbackName || ""}</b><br/>${a.street1 || ""}${l2}<br/>${a.city || ""}, ${a.state || ""} ${a.zip || ""}${a.phone ? `<br/>${a.phone}` : ""}`;
  };
  w.document.write(`
    <html><head><title>Shipping label ${label.tracking_number}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:24px;color:#10241c}
      .box{border:2px solid #0c4a38;border-radius:12px;padding:20px}
      h1{font-size:20px;margin:0 0 4px}.muted{color:#5c5c50;font-size:12px}
      .row{margin:12px 0;line-height:1.4}.big{font-size:22px;font-weight:800;letter-spacing:2px}
      .bar{height:56px;background:repeating-linear-gradient(90deg,#000 0 3px,#fff 3px 6px);margin-top:8px;border-radius:4px}
    </style></head><body>
    <div class="box">
      <h1>${label.carrier || "SouqStream Post"}</h1>
      <div class="muted">${label.service || ""}</div>
      <div class="row"><div class="muted">SHIP TO</div>${fmt(label.ship_to, label.to)}</div>
      <div class="row"><div class="muted">FROM</div>${fmt(label.ship_from, label.from)}</div>
      ${label.return_address ? `<div class="row"><div class="muted">RETURN ADDRESS</div>${fmt(label.return_address, label.from)}</div>` : ""}
      <div class="row"><div class="muted">ITEM</div>${label.item || ""}</div>
      <div class="row"><div class="muted">TRACKING</div><div class="big">${label.tracking_number}</div><div class="bar"></div></div>
    </div>
    <script>window.onload=()=>window.print()</script>
    </body></html>`);
  w.document.close();
}

function OrderModal({ orderId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("USPS");
  const [customCarrier, setCustomCarrier] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [note, setNote] = useState("");

  const load = () => api.get(`/orders/${orderId}`).then((d) => {
    setData(d);
    setTracking(d.order.tracking_number || "");
    const c = d.order.carrier;
    if (c && CARRIERS.includes(c)) setCarrier(c);
    else if (c) { setCarrier("Other"); setCustomCarrier(c); }
  }).catch(() => onClose());
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);
  if (!data) return <div className="ss-modal-back"><div className="ss-modal"><Loading /></div></div>;
  const o = data.order;
  const canCancel = !["shipped", "delivered", "refunded", "cancelled"].includes(o.status);
  const isClosed = ["cancelled", "refunded"].includes(o.status);
  const isIntl = !isUS((o.ship_to || {}).country);

  const saveTracking = async () => {
    setBusy(true);
    const chosen = carrier === "Other" ? (customCarrier.trim() || "Other") : carrier;
    try { await api.post(`/orders/${o.id}/tracking`, { tracking_number: tracking, carrier: chosen }); toast.success("Tracking saved — buyer notified"); await load(); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const label = async () => {
    setBusy(true);
    try { const res = await api.post(`/orders/${o.id}/label`); openLabelWindow(res.label); await load(); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const submitCancel = async () => {
    const note2 = note.trim();
    if (reason === "Other" && note2.length < 3) { toast.error("Please describe the reason"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/orders/${o.id}/cancel`, { reason, note: note2 });
      toast.success(res.refunded ? `Order cancelled · $${res.amount?.toFixed?.(2) || res.amount} refunded to the buyer` : "Order cancelled — buyer notified");
      setShowCancel(false); setNote(""); await load(); onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="order-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>Order details</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="order-modal-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <img src={o.image} alt="" style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800 }}>{o.item_title}</div>
            <div className="ss-muted" style={{ fontSize: 13, marginTop: 2 }}>Order {o.id.slice(-8)}</div>
            <div style={{ marginTop: 6 }}><StatusPill status={o.status} /></div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>{money(o.amount)}</div>
            <div className="ss-muted" style={{ fontSize: 12 }}>Net {money(o.seller_net)}</div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 14 }}>
          <div><div className="ss-muted" style={{ fontSize: 12 }}>Buyer</div><b>{o.buyer_name}</b></div>
          <div><div className="ss-muted" style={{ fontSize: 12 }}>Ordered</div>{new Date(o.created_at).toLocaleDateString()}</div>
          {o.return_status && <div><div className="ss-muted" style={{ fontSize: 12 }}>Return</div><span className="ss-pill pill--refunded">{o.return_status}</span></div>}
        </div>

        {o.status === "cancelled" && o.cancel_reason && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#fff6f5", borderLeft: "3px solid var(--red)" }} data-testid="order-cancel-reason">
            <div className="ss-muted" style={{ fontSize: 12, marginBottom: 2 }}>Cancelled · reason</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{o.cancel_reason}</div>
            {o.refund_amount ? <div className="ss-muted" style={{ fontSize: 13, marginTop: 4 }}>Refunded {money(o.refund_amount)} to the buyer</div> : null}
          </div>
        )}

        {o.ship_to && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "var(--gold-soft)" }} data-testid="order-ship-to">
            <div className="ss-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><MapPin size={13} /> Ship to</div>
            <div style={{ fontWeight: 700 }}>{o.ship_to.name}</div>
            <div style={{ fontSize: 14 }}>{o.ship_to.street1}{o.ship_to.street2 ? `, ${o.ship_to.street2}` : ""}</div>
            <div style={{ fontSize: 14 }}>{o.ship_to.city}{o.ship_to.state ? `, ${o.ship_to.state}` : ""} {o.ship_to.zip}</div>
            {isIntl && <div style={{ fontSize: 14, fontWeight: 700 }}>{countryName(o.ship_to.country)}</div>}
            {o.ship_to.phone && <div className="ss-muted" style={{ fontSize: 13 }}>{o.ship_to.phone}</div>}
          </div>
        )}

        {!isClosed && isIntl && (
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, background: "var(--gold-soft)", borderRadius: 10, padding: "10px 12px", fontSize: 13 }} data-testid="intl-order-note">
            <Globe size={15} style={{ color: "var(--gold-2)" }} />
            <span><b>International order.</b> Ship with your own carrier and add the carrier + tracking number below — buying a prepaid US label isn't available for this destination.</span>
          </div>
        )}

        {!isClosed && (
        <div style={{ marginTop: 18 }}>
          <label className="ss-label">Shipping carrier & tracking</label>
          <div className="ss-muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 8 }}>
            Shipping from abroad or with your own carrier? Pick the carrier and paste the tracking number — the buyer gets a live tracking link.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div className="ss-field" style={{ height: 46, minWidth: 180 }}>
              <Truck size={16} className="ss-muted" />
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="carrier-select"
                style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 14, cursor: "pointer" }}>
                {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {carrier === "Other" && (
              <div className="ss-field" style={{ height: 46, minWidth: 180 }}>
                <input placeholder="Carrier name (e.g. Saudi Post)" value={customCarrier} onChange={(e) => setCustomCarrier(e.target.value)} data-testid="carrier-custom-input" />
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <div className="ss-field" style={{ flex: 1, height: 46 }}>
              <input placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="tracking-input" />
            </div>
            <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={saveTracking} disabled={busy} data-testid="save-tracking">Save</button>
          </div>
          {o.tracking_number && o.tracking_url && (
            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="ss-muted" style={{ fontSize: 12, marginTop: 8, display: "inline-flex", alignItems: "center", gap: 5 }} data-testid="carrier-track-link">
              <Truck size={12} /> Track on {o.carrier} site
            </a>
          )}
        </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          {!isClosed && !isIntl && (
            <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={label} disabled={busy} data-testid="print-label"><Printer size={15} /> Print shipping label</button>
          )}
          <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate(`/messages/${o.buyer_id}`)} data-testid="message-buyer"><MessageSquare size={15} /> Message buyer</button>
          {canCancel && !showCancel && (
            <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => setShowCancel(true)} data-testid="cancel-order-btn"><Ban size={15} /> Cancel & refund</button>
          )}
        </div>

        {showCancel && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: "1px solid var(--red)", background: "#fff9f8" }} data-testid="cancel-panel">
            <div style={{ fontWeight: 800, marginBottom: 8, color: "var(--red)" }}>Cancel this order</div>
            <p className="ss-muted" style={{ fontSize: 13, marginBottom: 10 }}>
              The buyer will be refunded {money(o.amount)} to their original payment method and notified with your reason.
            </p>
            <label className="ss-label">Reason</label>
            <select className="ss-field" style={{ height: 44, width: "100%" }} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-select">
              {CANCEL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className="ss-label" style={{ marginTop: 10 }}>{reason === "Other" ? "Tell the buyer what happened" : "Add a note (optional)"}</label>
            <textarea className="ss-field" style={{ minHeight: 70, width: "100%", padding: 10 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Message shown to the buyer…" data-testid="cancel-note" />
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => setShowCancel(false)} disabled={busy} data-testid="cancel-abort">Keep order</button>
              <button className="ss-btn ss-btn-sm" style={{ background: "var(--red)", color: "#fff" }} onClick={submitCancel} disabled={busy} data-testid="cancel-confirm"><Ban size={15} /> {busy ? "Cancelling…" : "Cancel & refund buyer"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CancellationsCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/seller/cancellations").then(setData).catch(() => setData(null)); }, []);
  if (!data || data.total === 0) return null;
  const palette = ["#0c4a38", "#d4a017", "#b45309", "#5b8a72", "#8a6a08", "#c2410c"];
  return (
    <div className="ss-card" style={{ padding: 20, marginBottom: 18 }} data-testid="cancellations-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <div className="stat-ic" style={{ background: "#fff6f5" }}><TrendingDown size={20} color="var(--red)" /></div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Why orders get cancelled</div>
          <div className="ss-muted" style={{ fontSize: 13 }}>
            {data.total} cancelled · {money(data.lost_value)} in lost sales
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
        {data.breakdown.map((b, i) => (
          <div key={b.reason} data-testid={`cancel-reason-row-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{b.reason}</span>
              <span className="ss-muted">{b.count} · {b.pct}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: "var(--surface-3, #eee)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.max(b.pct, 4)}%`, background: palette[i % palette.length], borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SellerOrders() {
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("active");

  const load = () => api.get("/seller/orders").then((d) => setOrders(d.orders)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);
  if (orders === null) return <Loading />;

  const active = orders.filter((o) => o.status !== "cancelled");
  const cancelled = orders.filter((o) => o.status === "cancelled");
  const list = tab === "cancelled" ? cancelled : active;

  return (
    <div className="fade-up">
      <PageHead title="Seller Orders" sub="Click an order to view details, add tracking, print a label or message the buyer." />

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button className={`ss-btn ss-btn-sm ${tab === "active" ? "ss-btn-primary" : "ss-btn-ghost"}`} onClick={() => setTab("active")} data-testid="orders-tab-active">Active ({active.length})</button>
        <button className={`ss-btn ss-btn-sm ${tab === "cancelled" ? "ss-btn-primary" : "ss-btn-ghost"}`} onClick={() => setTab("cancelled")} data-testid="orders-tab-cancelled">Cancelled ({cancelled.length})</button>
      </div>

      {list.length === 0 ? (
        <Empty icon={Receipt} title={tab === "cancelled" ? "No cancelled orders" : "No orders yet"} sub={tab === "cancelled" ? "Orders you cancel will be listed here." : "Orders from your live shows will appear here."} />
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid={tab === "cancelled" ? "cancelled-orders-table" : "seller-orders-table"}>
            <thead><tr>
              <th>Item</th><th>Buyer</th><th>Amount</th>
              {tab === "cancelled" ? <><th>Reason</th><th>Refund</th></> : <><th>Net</th><th>Status</th></>}
            </tr></thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(o.id)} data-testid={`order-row-${o.id}`}>
                  <td style={{ fontWeight: 700 }}>{o.item_title}</td>
                  <td>{o.buyer_name}</td>
                  <td>{money(o.amount)}</td>
                  {tab === "cancelled" ? (
                    <>
                      <td style={{ maxWidth: 260, whiteSpace: "normal" }}>{o.cancel_reason || o.cancel_reason_category || "—"}</td>
                      <td>{o.refund_amount ? <span className="ss-pill pill--refunded">Refunded {money(o.refund_amount)}</span> : <span className="ss-muted">No charge</span>}</td>
                    </>
                  ) : (
                    <>
                      <td>{money(o.seller_net)}</td>
                      <td><StatusPill status={o.status} /></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && <OrderModal orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
